import {IMarketReport} from "api/marketReportApi/types";
import type {AnyAction} from "redux";
import {
	CLEAR_MARKET_REPORT_DETAILS,
	CREATE_MARKET_REPORT_DONE,
	CREATE_MARKET_REPORT_FAIL,
	CREATE_MARKET_REPORT_INIT,
	DELETE_MARKET_REPORT_DONE,
	DELETE_MARKET_REPORT_FAIL,
	DELETE_MARKET_REPORT_INIT,
	DISPLAY_DIVERSITY_PATHWAYS_LOADING,
	DISPLAY_DIVERSITY_STATS_LOADING,
	DISPLAY_MARKET_REPORT_MODAL,
	GET_MARKET_REPORTS_DONE,
	GET_MARKET_REPORTS_FAIL,
	GET_MARKET_REPORTS_INIT,
	RESET_MARKET_REPORT_FORM,
	RESET_MARKET_REPORT_FORM_DATA,
	SET_DIVERSITY_PATHWAYS_MODE,
	SET_DIVERSITY_PATHWAYS_REPORT,
	SET_DIVERSITY_PATHWAYS_SELECTED_TYPE,
	SET_DIVERSITY_STATS_REPORT,
	SET_DIVERSITY_STATS_SELECTED_TYPE,
	SET_MARKET_REPORT_DETAILS,
	SET_MARKET_REPORT_FILTER_VALUE_IN_FORM,
	SET_MARKET_REPORT_FORM_DATA,
	SET_MARKET_REPORT_MULTIPLE_FILTER_VALUE_IN_FORM,
	SET_MARKET_REPORT_NAME_IN_FORM,
	SET_MARKET_REPORTS_PAGINATION_INFO,
	SET_MR_COMPANIES_MODE,
	SET_MR_COMPANY_FILTER_CONFIG,
	SET_MR_COMPANY_TYPE_FILTER_CONFIG,
	SET_MR_DISABLED_FILTER_KEYS,
	SET_MR_HEADCOUNT_FILTER_CONFIG,
	SET_MR_INDUSTRIES_FILTER_CONFIG,
	SET_MR_JOB_FUNCTION_FILTER_CONFIG,
	SET_MR_JOB_TITLE_FILTER_MODE,
	SET_MR_JOB_TITLE_MODE,
	SET_MR_KEYWORD_FILTER_CONFIG,
	SET_MR_LOCATIONS_FILTER_CONFIG,
	SET_MR_REVENUE_FILTER_CONFIG,
	UPDATE_MARKET_REPORT_DONE,
	UPDATE_MARKET_REPORT_FAIL,
	UPDATE_MARKET_REPORT_INIT,
} from "store/mapXActionTypes";
import {TMarketReportReducerState} from "store/market-report/types";

export const marketReportReducerInitialState: TMarketReportReducerState = {
	loadingMarketReport: false,
	creatingMarketReport: false,
	updatingMarketReport: false,
	deletingMarketReport: false,
	duplicatingMarketReport: false,
	displayMarketReportModal: false,
	results: [],
	deletion_in_progress: [],
	pagination: null,
	createdBy: [],
	companyFilterConfig: {
		active_position: "current", // "current" | "previous" | "any"
	},
	candidateCompanyHeadcountFilterConfig: {
		active_position: "current", // "current" | "previous" | "any"
	},
	candidateCompanyRevenueFilterConfig: {
		active_position: "current", // "current" | "previous" | "any"
	},
	candidateCompanyTypeFilterConfig: {
		active_position: "current", // "current" | "previous" | "any"
	},
	candidateIndustriesFilterConfig: {
		active_position: "current", // "current" | "previous" | "any"
	},
	candidateLocationFilterConfig: {
		active_position: "current", // "current" | "previous" | "any"
	},
	candidateKeywordFilterConfig: {
		active_position: "current", // "current" | "previous" | "any"
		active_current_position_connect_logic: "or", // "and" | "or" | "not"
		active_previous_position_connect_logic: "or", // "and" | "or" | "not"
		active_any_position_connect_logic: "or", // "and" | "or" | "not"
	},
	candidateJobFunctionFilterConfig: {
		active_position: "current", // "current" | "previous" | "any"
		active_current_position_connect_logic: "or", // "and" | "or" | "not"
		active_previous_position_connect_logic: "or", // "and" | "or" | "not"
		active_any_position_connect_logic: "or", // "and" | "or" | "not"
	},
	jobTitleFilterMode: "contains", // contains | close
	marketReportForm: {
		name: "",
		filters: {
			companies: [],
			current_companies: [],
			previous_companies: [],
			current_seniority: [],

			suggested_job_titles: [],
			current_job_titles_or: [],

			specialisms_and: [],
			specialisms_or: [],
			specialisms_not: [],

			current_specialisms_and: [],
			current_specialisms_or: [],
			current_specialisms_not: [],

			previous_specialisms_and: [],
			previous_specialisms_or: [],
			previous_specialisms_not: [],

			text_keywords_or: [],
			text_keywords_and: [],
			text_keywords_not: [],

			current_text_keywords_or: [],
			current_text_keywords_and: [],
			current_text_keywords_not: [],

			previous_text_keywords_or: [],
			previous_text_keywords_and: [],
			previous_text_keywords_not: [],

			headquarters_countries: [],
			countries_or: [],
			previous_countries_or: [],
			current_countries_or: [],

			regions_or: [],
			previous_regions_or: [],
			current_regions_or: [],

			states_or: [],
			previous_states_or: [],
			current_states_or: [],

			cities_or: [],
			previous_cities_or: [],
			current_cities_or: [],

			languages: [],

			industries: [],
			previous_industries: [],
			current_industries: [],

			revenue: [],
			current_revenue: [],
			previous_revenue: [],

			company_size: [],
			current_company_size: [],
			previous_company_size: [],

			company_type: [],
			previous_company_type: [],
			current_company_type: [],

			company_events: {
				types: [],
				size: [],
				dates: [],
			},
		},
	},
	selectedCompanies: [],
	marketReportDetails: null,

	diversityStatsLoading: false,
	diversityStatsSelectedType: "gender",
	diversityStatsReport: null,
	diversePathwaysLoading: false,
	diversePathwaysSelectedType: "by_function",
	diversePathwaysMode: "Gender or ethnicity",
	diversePathwaysReport: null,
	disabledFilterKeysOnCriteriaBreakDown: [],
};

const marketReportReducer = (state = marketReportReducerInitialState, action: AnyAction) => {
	switch (action.type) {
		case DELETE_MARKET_REPORT_INIT: {
			const id = action.payload;

			return {
				...state,
				deletion_in_progress: [...state.deletion_in_progress, id],
			};
		}
		case DELETE_MARKET_REPORT_DONE: {
			const id = action.payload;

			return {
				...state,
				deletion_in_progress: state.deletion_in_progress.filter(
					(item: number) => item !== id,
				),
				results: state.results.filter((item) => item.id !== id),
			};
		}
		case DELETE_MARKET_REPORT_FAIL: {
			const {id} = action.payload;

			return {
				...state,
				deletion_in_progress: state.deletion_in_progress.filter(
					(item: number) => item !== id,
				),
			};
		}
		// case UPDATE_MARKET_MAP_INIT:
		//     return {
		//         ...state,
		//         creation_in_progress: true,
		//         creation_error: null,
		//     };
		// case UPDATE_MARKET_MAP_DONE: {
		//     const marketMap = action.payload;
		//
		//     return {
		//         ...state,
		//         creation_in_progress: false,
		//         // @TODO: Define server types for TRootState
		//         // eslint-disable-next-line @typescript-eslint/no-explicit-any
		//         results: state.results.map((item: any) =>
		//             item.id === marketMap.id ? marketMap : item,
		//         ),
		//         // @TODO: Define server types for TRootState
		//         // eslint-disable-next-line @typescript-eslint/no-explicit-any
		//         modal_results: state.modal_results.map((item: any) =>
		//             item.id === marketMap.id ? marketMap : item,
		//         ),
		//         edit_market_map_modal: false,
		//     };
		// }
		// case UPDATE_MARKET_MAP_FAIL:
		//     return {
		//         ...state,
		//         creation_in_progress: false,
		//         creation_error: action.payload,
		//     };

		case RESET_MARKET_REPORT_FORM:
			return {
				...state,
				marketReportForm: {
					...marketReportReducerInitialState.marketReportForm,
				},
			};

		case DISPLAY_MARKET_REPORT_MODAL:
			return {
				...state,
				displayMarketReportModal: action.payload,
			};

		case SET_MARKET_REPORT_NAME_IN_FORM:
			return {
				...state,
				marketReportForm: {
					...state.marketReportForm,
					name: action.payload,
				},
			};

		case SET_MARKET_REPORT_FILTER_VALUE_IN_FORM:
			return {
				...state,
				marketReportForm: {
					...state.marketReportForm,
					filters: {
						...state.marketReportForm.filters,
						company_events:
							action.payload?.filters?.company_events !== undefined
								? {
										...marketReportReducerInitialState.marketReportForm.filters
											.company_events,
										...action.payload.filters.company_events,
								  }
								: state.marketReportForm.filters.company_events,
						[action.payload.key]: action.payload.value,
					},
				},
			};
		case SET_MARKET_REPORT_MULTIPLE_FILTER_VALUE_IN_FORM:
			return {
				...state,
				marketReportForm: {
					...state.marketReportForm,
					filters: {
						...state.marketReportForm.filters,
						...action.payload,
						company_events:
							action.payload?.company_events !== undefined
								? {
										...marketReportReducerInitialState.marketReportForm.filters
											.company_events,
										...action.payload.company_events,
								  }
								: state.marketReportForm.filters.company_events,
					},
				},
			};
		case SET_MARKET_REPORT_FORM_DATA:
			return {
				...state,
				marketReportForm: {
					...state.marketReportForm,
					...action.payload,
					filters: {
						...marketReportReducerInitialState.marketReportForm.filters,
						...action.payload.filters,
						company_events:
							action.payload?.filters?.company_events !== undefined
								? {
										...marketReportReducerInitialState.marketReportForm.filters
											.company_events,
										...action.payload.filters.company_events,
								  }
								: state.marketReportForm.filters.company_events,
					},
				},
				jobTitleFilterMode: action.payload?.filters?.job_title_filter_mode || "contains",
			};
		case RESET_MARKET_REPORT_FORM_DATA:
			return {
				...state,
				marketReportForm: {
					...marketReportReducerInitialState.marketReportForm,
				},
				selectedCompanies: [],
			};
		case SET_MR_COMPANY_FILTER_CONFIG:
			return {
				...state,
				companyFilterConfig: {
					...state.companyFilterConfig,
					...action.payload,
				},
			};
		case SET_MR_JOB_FUNCTION_FILTER_CONFIG:
			return {
				...state,
				candidateJobFunctionFilterConfig: {
					...state.candidateJobFunctionFilterConfig,
					...action.payload,
				},
			};

		case SET_MR_INDUSTRIES_FILTER_CONFIG:
			return {
				...state,
				candidateIndustriesFilterConfig: {
					...state.candidateIndustriesFilterConfig,
					...action.payload,
				},
			};

		case SET_MR_LOCATIONS_FILTER_CONFIG:
			return {
				...state,
				candidateLocationFilterConfig: {
					...state.candidateLocationFilterConfig,
					...action.payload,
				},
			};

		case SET_MR_KEYWORD_FILTER_CONFIG:
			return {
				...state,
				candidateKeywordFilterConfig: {
					...state.candidateKeywordFilterConfig,
					...action.payload,
				},
			};

		case SET_MR_JOB_TITLE_FILTER_MODE:
			return {
				...state,
				job_title_filter_mode: action.payload,
			};

		case SET_MR_HEADCOUNT_FILTER_CONFIG:
			return {
				...state,
				candidateCompanyHeadcountFilterConfig: {
					...state.candidateCompanyHeadcountFilterConfig,
					...action.payload,
				},
			};

		case SET_MR_REVENUE_FILTER_CONFIG:
			return {
				...state,
				candidateCompanyRevenueFilterConfig: {
					...state.candidateCompanyRevenueFilterConfig,
					...action.payload,
				},
			};

		case SET_MR_COMPANY_TYPE_FILTER_CONFIG:
			return {
				...state,
				candidateCompanyTypeFilterConfig: {
					...state.candidateCompanyTypeFilterConfig,
					...action.payload,
				},
			};
		case SET_MR_JOB_TITLE_MODE:
			return {
				...state,
				jobTitleFilterMode: action.payload,
			};
		case SET_MR_COMPANIES_MODE:
			return {
				...state,
				selectedCompanies: action.payload,
			};

		case CREATE_MARKET_REPORT_INIT:
			return {
				...state,
				creatingMarketReport: true,
			};
		case CREATE_MARKET_REPORT_DONE: {
			const marketReport = action.payload;

			return {
				...state,
				creatingMarketReport: false,
				displayMarketReportModal: false,
				results: [marketReport, ...state.results],
				marketReportForm: {
					id: marketReport.id,
					name: marketReport.name,
					filters: {
						...marketReportReducerInitialState.marketReportForm.filters,
						...marketReport.filters,
						company_events:
							marketReport.filters?.company_events !== undefined
								? {
										...marketReportReducerInitialState.marketReportForm.filters
											.company_events,
										...marketReport.filters.company_events,
								  }
								: marketReportReducerInitialState.marketReportForm.filters
										.company_events,
					},
				},
				marketReportDetails: marketReport,
				jobTitleFilterMode: marketReport?.filters?.job_title_filter_mode || "contains",
				disabledFilterKeysOnCriteriaBreakDown: [],
			};
		}
		case CREATE_MARKET_REPORT_FAIL:
			return {
				...state,
				creatingMarketReport: false,
			};

		case SET_MARKET_REPORT_DETAILS:
			return {
				...state,
				marketReportDetails: action.payload,
				marketReportForm: {
					name: action.payload.name,
					filters: {
						...marketReportReducerInitialState.marketReportForm.filters,
						...action.payload.filters,
						company_events:
							action.payload?.filters?.company_events !== undefined
								? {
										...marketReportReducerInitialState.marketReportForm.filters
											.company_events,
										...action.payload.filters.company_events,
								  }
								: state.marketReportForm.filters.company_events,
					},
					id: action.payload.id,
				},
				jobTitleFilterMode: action.payload?.filters?.job_title_filter_mode || "contains",
				disabledFilterKeysOnCriteriaBreakDown: [],
			};
		case CLEAR_MARKET_REPORT_DETAILS:
			return {
				...state,
				marketReportDetails: marketReportReducerInitialState.marketReportDetails,
				marketReportForm: marketReportReducerInitialState.marketReportForm,
				diversityStatsReport: marketReportReducerInitialState.diversityStatsReport,
				diversePathwaysReport: marketReportReducerInitialState.diversePathwaysReport,
				jobTitleFilterMode: "contains",
				disabledFilterKeysOnCriteriaBreakDown: [],
			};
		case UPDATE_MARKET_REPORT_INIT:
			return {
				...state,
				updatingMarketReport: true,
			};
		case UPDATE_MARKET_REPORT_DONE:
			const marketReport = action.payload;

			return {
				...state,
				updatingMarketReport: false,
				displayMarketReportModal: false,
				results: [
					marketReport,
					...state.results.filter((item: IMarketReport) => item.id !== marketReport.id),
				],
				marketReportForm: {
					id: marketReport.id,
					name: marketReport.name,
					filters: {
						...marketReportReducerInitialState.marketReportForm.filters,
						...marketReport.filters,
						company_events:
							marketReport.filters?.company_events !== undefined
								? {
										...marketReportReducerInitialState.marketReportForm.filters
											.company_events,
										...marketReport.filters.company_events,
								  }
								: marketReportReducerInitialState.marketReportForm.filters
										.company_events,
					},
				},
				marketReportDetails: marketReport,
				jobTitleFilterMode: marketReport?.filters?.job_title_filter_mode || "contains",
				disabledFilterKeysOnCriteriaBreakDown: [],
			};
		case UPDATE_MARKET_REPORT_FAIL: {
			return {
				...state,
				updatingMarketReport: false,
			};
		}

		case GET_MARKET_REPORTS_INIT:
			return {
				...state,
				loadingMarketReport: true,
			};
		case GET_MARKET_REPORTS_DONE:
			const {
				data: {pagination, results},
				name,
				createdBy,
			} = action.payload;

			return {
				...state,
				name,
				created_by: createdBy,
				results: results,
				pagination: pagination || {},
				loadingMarketReport: false,
			};
		case GET_MARKET_REPORTS_FAIL:
			return {
				...state,
				loadingMarketReport: false,
			};
		case SET_MARKET_REPORTS_PAGINATION_INFO:
			return {
				...state,
				pagination: action.payload,
			};
		case DISPLAY_DIVERSITY_STATS_LOADING:
			return {
				...state,
				diversityStatsLoading: action.payload,
			};
		case SET_DIVERSITY_STATS_SELECTED_TYPE:
			return {
				...state,
				diversityStatsSelectedType: action.payload,
			};
		case SET_DIVERSITY_STATS_REPORT:
			return {
				...state,
				diversityStatsReport: action.payload,
				diversityStatsLoading: false,
			};
		case DISPLAY_DIVERSITY_PATHWAYS_LOADING:
			return {
				...state,
				diversePathwaysLoading: action.payload,
			};
		case SET_DIVERSITY_PATHWAYS_SELECTED_TYPE:
			return {
				...state,
				diversePathwaysSelectedType: action.payload,
			};
		case SET_DIVERSITY_PATHWAYS_MODE:
			return {
				...state,
				diversePathwaysMode: action.payload,
			};
		case SET_DIVERSITY_PATHWAYS_REPORT:
			return {
				...state,
				diversePathwaysReport: action.payload,
				diversePathwaysLoading: false,
			};
		case SET_MR_DISABLED_FILTER_KEYS:
			return {
				...state,
				disabledFilterKeysOnCriteriaBreakDown: action.payload,
			};
		default:
			return state;
	}
};

export default marketReportReducer;
